import React, { useState, useEffect, useRef } from 'react';

// Define the option type (assuming each option has dynamic keys)
interface Option {
  [key: string]: any;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValue: string[];
  type: 'arrayWithObject' | 'simpleArray';
  placeholder?: string;
  onChange: (option: any, type: 'arrayWithObject' | 'simpleArray') => void;
  getParam?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options = [],
  selectedValue = [],
  type,
  placeholder,
  onChange,
  getParam = '',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState('');

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Extract available params (keys) from the options array
  const availableParams = options.length > 0 ? Object.keys(options[0]) : [];

  useEffect(() => {
    if (type === 'arrayWithObject' && !getParam) {
      setError('Error: getParam is required for arrayWithObject type.');
    } else {
      setError('');
    }
  }, [type, getParam, options]);

  // Handle option selection
  const handleSelect = (option: any) => {
    if (type === 'arrayWithObject' && getParam) {
      onChange(option[getParam], type);
    } else {
      onChange(option, type);
    }
  };

  // Handle search input
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Filter options based on the search query
  const filteredOptions = options.filter((option) =>
    Object.values(option).some((value) =>
      value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Handle dropdown toggle
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Handle click outside dropdown to close it
  const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', maxWidth: 300 }}>
      {/* Error message */}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {/* Search input and dropdown toggle */}
      <div style={{ position: 'relative' }}>
        <div
          className="dropdown-input"
          style={{
            border: '1px solid #ccc',
            padding: '5px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={toggleDropdown}
        >
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder={placeholder || 'Select an option'}
            style={{
              border: 'none',
              outline: 'none',
              flex: 1,
              backgroundColor: 'transparent',
            }}
          />
          <span>{isOpen ? '▲' : '▼'}</span>
        </div>

        {/* Dropdown menu */}
        {isOpen && (
          <div
            className="dropdown-menu"
            style={{
              border: '1px solid #ccc',
              marginTop: '5px',
              padding: '5px',
              maxHeight: '200px',
              overflowY: 'auto',
              position: 'absolute',
              backgroundColor: 'white',
              width: '100%',
              zIndex: 10,
            }}
          >
            {/* Display filtered options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className="dropdown-option"
                  onClick={() => handleSelect(option)}
                  style={{ padding: '8px', cursor: 'pointer' }}
                >
                  {getParam ? option[getParam] : JSON.stringify(option)}
                </div>
              ))
            ) : (
              <div style={{ padding: '8px' }}>No data</div>
            )}
          </div>
        )}
      </div>

      {/* Display selected items */}
      <div style={{ marginTop: '10px' }}>
        {selectedValue.map((item, index) => (
          <span key={index} style={{ marginRight: '10px', padding: '5px', background: '#007bff', color: '#fff' }}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
